import React, { useRef, useId } from 'react';
import cn from 'classnames';

import type { InputProps } from './types';
import { Container, Label, Hint, Addon, ClearButton, LoadingIcon } from './components';
import { setInputValue, emitChangeEvent, getOuterVerticalAlign, useControlled } from './utils';

const Input = (props: InputProps, ref: React.Ref<HTMLDivElement>) => {
  const {
    id,
    name,
    type = 'text',
    value,
    defaultValue,
    label,
    placeholder,
    hint,
    inputProps,
    labelClassName,
    inputClassName,
    hintClassName,
    disabled = false,
    readOnly = false,
    required = false,
    allowClear = false,
    block = false,
    loading = false,
    clearIcon,
    clearClassName,
    asteriskIcon,
    asteriskPosition = 'right',
    asteriskClassName,
    prependOuter,
    prependInner,
    appendInner,
    appendOuter,
    prependOuterClassName,
    prependInnerClassName,
    appendInnerClassName,
    appendOuterClassName,
    onChange,
    onClear,
    ...rest
  }: InputProps = props;

  const inputRef = useRef<HTMLInputElement>(null);

  const [valueState, setValueState] = useControlled({
    controlledValue: value,
    defaultUncontrolledValue: defaultValue,
    onChange,
  });

  const generatedId = useId();
  const inputId = id ?? generatedId;

  const hasValue = !!valueState;
  const hasLabel = label != null;
  const hasHint = hint != null;

  const inactive = disabled || readOnly;
  const showClearButton = allowClear && !inactive;

  const outerVerticalAlign = getOuterVerticalAlign(hasLabel);

  const handleBoxMouseDown = (e: React.MouseEvent<HTMLDivElement>) => {
    const isClickYourself = e.target === e.currentTarget; // Щелкнули по себе

    if (isClickYourself) {
      e.preventDefault(); // Предотвращение потери фокуса input, если он был в фокусе
      inputRef.current?.focus();
    }
  };

  const handleClear = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault(); // Предотвращаем потерю фокуса для input, если в поле был курсор

    if (inputRef.current && hasValue) {
      // При щелчке нет события для onChange. Не используем псевдо-событие, а формируем оригинальное

      // Очистка значения у элемента (неконтролируемое изменение)
      setInputValue(inputRef.current, '');

      // Испускаем событие для вызова обработчика onChange у input (контролируемое изменение)
      emitChangeEvent(inputRef.current);

      onClear?.(valueState);
    }
  };

  return (
    <Container ref={ref} disabled={disabled} block={block} {...rest}>
      <Addon.Prepend className={cn(outerVerticalAlign, prependOuterClassName)}>
        {prependOuter}
      </Addon.Prepend>

      <div className="e-input__content">
        {hasLabel && (
          <Label
            htmlFor={inputId}
            className={labelClassName}
            required={required}
            asteriskIcon={asteriskIcon}
            asteriskPosition={asteriskPosition}
            asteriskClassName={asteriskClassName}
          >
            {label}
          </Label>
        )}

        <div className="e-input__box" onMouseDown={disabled ? undefined : handleBoxMouseDown}>
          <Addon.Prepend className={prependInnerClassName}>{prependInner}</Addon.Prepend>
          <input
            {...inputProps}
            id={inputId}
            ref={inputRef}
            value={valueState}
            placeholder={placeholder}
            type={type}
            name={name}
            className={cn('e-input__control', inputClassName)}
            disabled={disabled}
            readOnly={readOnly}
            required={required}
            onChange={setValueState}
          />
          {showClearButton && (
            <ClearButton
              visible={hasValue}
              clearIcon={clearIcon}
              className={clearClassName}
              onMouseDown={handleClear}
            />
          )}
          {loading && <LoadingIcon className="e-input__icon e-input__icon--loading" />}
          <Addon.Append className={appendInnerClassName}>{appendInner}</Addon.Append>
        </div>

        {hasHint && <Hint className={hintClassName}>{hint}</Hint>}
      </div>

      <Addon.Append className={cn(outerVerticalAlign, appendOuterClassName)}>
        {appendOuter}
      </Addon.Append>
    </Container>
  );
};

export default React.forwardRef(Input);
